import { useState, useCallback, useRef, useEffect } from 'react';
import { memedApi } from '@/api/memed';
import { mapPatientToMemed, validateMemedPatient, type MemedPatientPayload } from './memed/memed-patient.mapper';
import { mapAllergiesToMemed, mapAllergiesToMemedIds, type MedikeiAllergy } from './memed/memed-allergy.mapper';
import { mapClinicalConditionsToMemed, type MedikeiChronicCondition } from './memed/memed-condition.mapper';
import type { MemedAdditionalDataPayload } from './memed/memed-additional-data.mapper';
import type { AddItemPayload } from './memed/memed-item.mapper';

const PRESCRIPTION_MODULE = 'plataforma.prescricao';
const SDK_MODULE = 'plataforma.sdk';
const MEMED_SCRIPT_ID = 'memed-prescricao-script';
const MEMED_SCRIPT_URL =
  import.meta.env.VITE_MEMED_ENV === 'production'
    ? 'https://partners.memed.com.br/integration.js'
    : 'https://integrations.memed.com.br/modulos/plataforma.sinapse-prescricao/build/sinapse-prescricao.min.js';
const CONDITION_ALERTS_ERROR = 'Não foi possível preparar as condições clínicas do paciente para a prescrição.';
const MODULE_READY_TIMEOUT_ERROR = 'Tempo limite excedido ao aguardar o carregamento do módulo da MeMed.';
const RECEITUARIO_THEME_MIN = 1;
const RECEITUARIO_THEME_MAX = 4;

export interface MemedWorkplace {
  city?: string;
  state?: string;
  cnes?: string;
  local_name?: string;
  address?: string;
  phone?: string;
}

export interface Medicine {
  id: string;
  nome?: string;
  composicao?: string;
  descricao?: string;
  fabricante?: string;
  forma_fisica?: string;
  quantidade?: number;
  tipo?: string;
  alto_custo?: boolean;
  controle_especial?: boolean;
}

export interface MemedPrescriptionEventMap {
  medicamentoAdicionado: Medicine;
  medicamentoRemovido: string;
  prescricaoExcluida: number;
  prescricaoImpressa: Record<string, unknown>;
  'core:moduleHide': unknown;
}

export interface MemedRichMedication {
  id?: string;
  nome: string;
  descricao?: string;
  principio_ativo?: string;
  posologia?: string;
  sanitized_posology?: string;
  quantidade?: number | string;
  unidade?: string;
  fabricante?: string;
  controle_especial?: boolean;
  receituario?: string;
  [key: string]: unknown;
}

export interface ExtractedMemedPrescriptionData {
  memedPrescriptionId: string | null;
  medications: MemedRichMedication[];
  digitalPrescriptionLink?: string;
  unlockCode?: string;
}

const PRESCRIPTION_ID_KEYS = ['id', 'prescriptionId', 'numero', 'prescription_id', 'prescriptionUuid', 'uuid'] as const;

export function extractMemedPrescriptionId(payload: unknown): string | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const root = payload as Record<string, unknown>;
  const prescricao = root.prescricao;

  if (typeof prescricao === 'object' && prescricao !== null) {
    const prescricaoData = prescricao as Record<string, unknown>;
    for (const key of ['id', 'prescriptionUuid'] as const) {
      const value = prescricaoData[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
  }

  for (const key of PRESCRIPTION_ID_KEYS) {
    const value = root[key];
    if (typeof value === 'string' && value.length > 0) {
      return value;
    }
  }

  return null;
}

export function extractMemedPrescriptionData(payload: unknown): ExtractedMemedPrescriptionData {
  const memedPrescriptionId = extractMemedPrescriptionId(payload);
  if (!payload || typeof payload !== 'object') {
    return { memedPrescriptionId, medications: [] };
  }

  const root = payload as Record<string, unknown>;
  const prescricao = (root.prescricao && typeof root.prescricao === 'object'
    ? root.prescricao
    : {}) as Record<string, unknown>;

  const digitalPrescriptionLink =
    (typeof root.digitalPrescriptionLink === 'string' && root.digitalPrescriptionLink) ||
    (typeof root.link === 'string' && root.link) ||
    (typeof prescricao.digitalPrescriptionLink === 'string' && prescricao.digitalPrescriptionLink) ||
    (typeof prescricao.link === 'string' && prescricao.link) ||
    undefined;

  const unlockCode =
    (typeof root.unlockCode === 'string' && root.unlockCode) ||
    (typeof root.unlock_code === 'string' && root.unlock_code) ||
    (typeof root.pin === 'string' && root.pin) ||
    (typeof prescricao.unlockCode === 'string' && prescricao.unlockCode) ||
    (typeof prescricao.unlock_code === 'string' && prescricao.unlock_code) ||
    (typeof prescricao.pin === 'string' && prescricao.pin) ||
    undefined;

  let rawMedications: unknown[] = [];
  if (Array.isArray(prescricao.medicamentos)) {
    rawMedications = prescricao.medicamentos;
  } else if (Array.isArray(root.medicamentos)) {
    rawMedications = root.medicamentos;
  } else if (Array.isArray(prescricao.itens)) {
    rawMedications = prescricao.itens;
  } else if (Array.isArray(root.itens)) {
    rawMedications = root.itens;
  } else if (Array.isArray(prescricao.items)) {
    rawMedications = prescricao.items;
  } else if (Array.isArray(root.items)) {
    rawMedications = root.items;
  }

  const medications: MemedRichMedication[] = rawMedications.map((item, idx) => {
    if (!item || typeof item !== 'object') {
      return { nome: String(item || '') };
    }
    const rawObj = item as Record<string, unknown>;
    const attr = (rawObj.attributes && typeof rawObj.attributes === 'object'
      ? rawObj.attributes
      : rawObj) as Record<string, unknown>;

    const nome = String(attr.nome || attr.name || '');
    const descricao = String(attr.descricao || attr.description || attr.principio_ativo || attr.active_ingredient || '');
    const principio_ativo = String(attr.principio_ativo || attr.active_ingredient || '');
    const posologia = String(attr.posologia || attr.posology || attr.dosage || attr.instrucoes || '');
    const sanitized_posology = String(
      attr.sanitized_posology || attr.sanitizedPosology || attr.posologia || attr.dosage || ''
    );
    const quantidade = attr.quantidade !== undefined ? (attr.quantidade as number | string) : (attr.quantity as number | string | undefined);
    const unidade = String(attr.unidade || attr.unit || '');
    const fabricante = String(attr.fabricante || attr.manufacturer || attr.laboratorio || '');
    const controle_especial = Boolean(
      attr.controle_especial ?? attr.controleEspecial ?? attr.controlled ?? false
    );
    const receituario = String(attr.receituario || attr.prescription_type || attr.recipe_type || '');

    return {
      id: String(rawObj.id || attr.id || `med-${idx}`),
      nome,
      descricao: descricao || undefined,
      principio_ativo: principio_ativo || undefined,
      posologia: posologia || undefined,
      sanitized_posology: sanitized_posology || undefined,
      quantidade,
      unidade: unidade || undefined,
      fabricante: fabricante || undefined,
      controle_especial,
      receituario: receituario || undefined,
      ...attr,
    };
  });

  return {
    memedPrescriptionId,
    medications,
    digitalPrescriptionLink,
    unlockCode,
  };
}

interface MemedPrescriptionContext {
  appointmentId?: string;
  professionalId?: string;
}

interface MemedPrescriptionOptions {
  featureToggles?: Record<string, boolean | string[]>;
  additionalData?: MemedAdditionalDataPayload;
  items?: AddItemPayload[];
  receituarioTheme?: number;
  token?: string;
}

export interface AddItemsResult {
  success: boolean;
  itemsSent: number;
  error?: string;
}

interface MdSinapsePrescricao {
  event?: {
    add: (event: string, handler: (data: unknown) => void) => void;
    remove?: (event: string, handler?: (data: unknown) => void) => void;
  };
  setToken?: (token: string) => void;
  hasToken?: () => boolean;
  getToken?: () => string;
}

interface MdHubCommand {
  send(module: string, command: string, payload?: unknown): Promise<unknown>;
}

interface MdHubModule {
  show(module: string): Promise<unknown>;
  hide(module: string): Promise<unknown>;
}

interface MdHubEvent {
  add(event: string, handler: (data: unknown) => void): void;
  remove?: (event: string, handler?: (data: unknown) => void) => void;
}

interface MdHub {
  command: MdHubCommand;
  module: MdHubModule;
  event?: MdHubEvent;
}

function logConditionAlerts(
  event: 'STARTED' | 'SUCCESS' | 'FAILED',
  payload: {
    patientId: string;
    workplaceId?: string;
    appointmentId?: string;
    professionalId?: string;
    durationMs?: number;
  }
): void {
  console.log(`[MEMED_CONDITION_ALERTS_${event}]`, JSON.stringify({ status: event, ...payload }));
}

function logPrescriptionPrinted(memedPrescriptionId: string): void {
  console.log('[MEMED_PRESCRIPTION_PRINTED]', JSON.stringify({ memedPrescriptionId }));
}

function maskToken(token?: string | null): string {
  if (!token) return 'null';
  if (token.length <= 8) return '***';
  return `${token.substring(0, 4)}...${token.substring(token.length - 4)}`;
}

declare global {
  interface Window {
    MdHub?: MdHub;
    MdSinapsePrescricao?: MdSinapsePrescricao;
  }
}

export function useMemedPrescription() {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isModuleReady, setIsModuleReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSettingPatient, setIsSettingPatient] = useState(false);
  const [isSettingAllergies, setIsSettingAllergies] = useState(false);
  const scriptLoadedRef = useRef(false);
  const moduleReadyRef = useRef(false);
  const moduleInitWaitersRef = useRef<Array<() => void>>([]);
  const moduleInitListenerRegisteredRef = useRef(false);
  const memedEventHandlersRef = useRef<Record<string, Set<(data: unknown) => void>>>({});
  const memedEventListenersRegisteredRef = useRef<Set<keyof MemedPrescriptionEventMap>>(new Set());
  const registeredDispatchersRef = useRef<Map<string, (data: unknown) => void>>(new Map());
  const activeTokenRef = useRef<string | null>(null);

  const registerMemedEventListeners = useCallback((): void => {
    const mdHub = window.MdHub;
    const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
      .MdSinapsePrescricao;

    (Object.keys(memedEventHandlersRef.current) as Array<keyof MemedPrescriptionEventMap>).forEach((event) => {
      const handlers = memedEventHandlersRef.current[event];

      if (!handlers || handlers.size === 0) {
        return;
      }

      if (memedEventListenersRegisteredRef.current.has(event)) {
        return;
      }

      if (event === 'core:moduleHide') {
        if (!sinapse?.event?.add) {
          return;
        }

        const dispatchHide = (data: unknown) => {
          if (typeof document !== 'undefined') {
            document.body.classList.remove('memed-module-open');
          }
          const subscribed = memedEventHandlersRef.current['core:moduleHide'];
          if (!subscribed) {
            return;
          }

          subscribed.forEach((handler) => {
            try {
              handler(data as never);
            } catch {
              // Handler errors must not break the event bus
            }
          });
        };

        sinapse.event.add('core:moduleHide', dispatchHide);
        memedEventListenersRegisteredRef.current.add('core:moduleHide');
        registeredDispatchersRef.current.set('core:moduleHide', dispatchHide);
        return;
      }

      if (!mdHub?.event?.add) {
        return;
      }

      const dispatchHubEvent = (data: unknown) => {
        const subscribed = memedEventHandlersRef.current[event];

        if (!subscribed) {
          return;
        }

        if (event === 'prescricaoImpressa') {
          const memedPrescriptionId = extractMemedPrescriptionId(data);
          if (memedPrescriptionId) {
            logPrescriptionPrinted(memedPrescriptionId);
          }
        }

        subscribed.forEach((handler) => {
          try {
            handler(data as never);
          } catch {
            // Handler errors must not break the event bus
          }
        });
      };

      mdHub.event.add(event, dispatchHubEvent);
      memedEventListenersRegisteredRef.current.add(event);
      registeredDispatchersRef.current.set(event, dispatchHubEvent);
    });
  }, []);

  const loadScript = useCallback(async (token?: string): Promise<boolean> => {
    let activeToken = token || activeTokenRef.current || undefined;
    if (!activeToken) {
      try {
        console.info('[MEMED_DIAGNOSTIC] Buscando token do médico via memedApi.getToken()...');
        const tokenRes = await memedApi.getToken();
        if (tokenRes?.token) {
          activeToken = tokenRes.token;
          activeTokenRef.current = activeToken;
          console.info('[MEMED_DIAGNOSTIC] ✓ Token obtido com sucesso:', maskToken(activeToken));
        }
      } catch (err) {
        console.warn('[MEMED_DIAGNOSTIC] ⚠️ Não foi possível obter o token da MeMed via API:', err);
      }
    } else {
      activeTokenRef.current = activeToken;
      console.info('[MEMED_DIAGNOSTIC] Utilizando token fornecido:', maskToken(activeToken));
    }

    const existingScript = document.getElementById(MEMED_SCRIPT_ID) as HTMLScriptElement | null;
    if (existingScript) {
      const existingToken = existingScript.getAttribute('data-token');
      console.info('[MEMED_DIAGNOSTIC] Tag script MeMed já presente no DOM. Token existente:', maskToken(existingToken), 'Novo token:', maskToken(activeToken));
      if (activeToken && existingToken !== activeToken) {
        console.info('[MEMED_DIAGNOSTIC] Removendo tag script MeMed desatualizada...');
        existingScript.remove();
        scriptLoadedRef.current = false;
        moduleReadyRef.current = false;
        setIsScriptLoaded(false);
        setIsModuleReady(false);
      } else {
        if (activeToken && !existingToken) {
          existingScript.setAttribute('data-token', activeToken);
          existingScript.dataset.token = activeToken;
        }
        scriptLoadedRef.current = true;
        setIsScriptLoaded(true);

        if (activeToken) {
          try {
            const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
              .MdSinapsePrescricao;
            if (sinapse?.setToken) {
              console.info('[MEMED_DIAGNOSTIC] Chamando MdSinapsePrescricao.setToken...');
              sinapse.setToken(activeToken);
            }
          } catch {
            // Silencioso
          }
        }

        if (window.MdHub) {
          moduleReadyRef.current = true;
          setIsModuleReady(true);
          registerMemedEventListeners();
        }
        return true;
      }
    }

    return new Promise((resolve) => {
      try {
        console.info('[MEMED_DIAGNOSTIC] Injetando tag <script> oficial da MeMed no <head> com token:', maskToken(activeToken));
        const script = document.createElement('script');
        script.id = MEMED_SCRIPT_ID;
        script.type = 'text/javascript';
        script.async = true;

        if (activeToken) {
          script.setAttribute('data-token', activeToken);
          script.dataset.token = activeToken;
        }
        script.setAttribute('data-color', '#0ea5e9');
        script.src = MEMED_SCRIPT_URL;

        script.onload = () => {
          console.info('[MEMED_DIAGNOSTIC] ✓ Script MeMed carregado (onload). MdHub presente:', Boolean(window.MdHub), 'MdSinapse:', Boolean((window as any).MdSinapsePrescricao));
          scriptLoadedRef.current = true;
          setIsScriptLoaded(true);

          if (activeToken) {
            try {
              const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
                .MdSinapsePrescricao;
              if (sinapse?.setToken) {
                console.info('[MEMED_DIAGNOSTIC] Invocando MdSinapsePrescricao.setToken após onload...');
                sinapse.setToken(activeToken);
              }
            } catch {
              // Silencioso
            }
          }

          if (window.MdHub) {
            moduleReadyRef.current = true;
            setIsModuleReady(true);
            registerMemedEventListeners();
          }
          resolve(true);
        };

        script.onerror = () => {
          console.error('[MEMED_DIAGNOSTIC] ❌ Falha ao baixar script da MeMed de:', MEMED_SCRIPT_URL);
          setError('Falha ao carregar script da MeMed');
          resolve(false);
        };

        document.head.appendChild(script);
      } catch (err) {
        console.error('[MEMED_DIAGNOSTIC] ❌ Erro ao criar elemento script MeMed:', err);
        setError('Erro ao carregar script da MeMed');
        resolve(false);
      }
    });
  }, [registerMemedEventListeners]);

  const handleModuleInit = useCallback((module?: { name?: string }) => {
    if (module && module.name !== PRESCRIPTION_MODULE) {
      return;
    }

    // Sincroniza token ativo no módulo inicializado
    if (activeTokenRef.current) {
      try {
        const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
          .MdSinapsePrescricao;
        if (sinapse?.setToken) {
          sinapse.setToken(activeTokenRef.current);
        }
      } catch (err) {
        console.warn('[useMemedPrescription] Erro ao chamar MdSinapsePrescricao.setToken em moduleInit:', err);
      }

      try {
        if (window.MdHub?.command?.send) {
          window.MdHub.command.send(PRESCRIPTION_MODULE, 'setToken', activeTokenRef.current).catch(() => {});
        }
      } catch {
        // Silencioso se comando setToken não for suportado no MdHub
      }
    }

    moduleReadyRef.current = true;
    setIsModuleReady(true);
    registerMemedEventListeners();
    moduleInitWaitersRef.current.forEach((resolve) => resolve());
    moduleInitWaitersRef.current = [];
  }, [registerMemedEventListeners]);

  const registerModuleInitListener = useCallback((): void => {
    if (moduleInitListenerRegisteredRef.current) {
      return;
    }

    const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
      .MdSinapsePrescricao;

    if (!sinapse?.event?.add) {
      return;
    }

    try {
      sinapse.event.add('core:moduleInit', handleModuleInit as (data: unknown) => void);
      moduleInitListenerRegisteredRef.current = true;
    } catch {
      // Events may not be available in all versions
    }
  }, [handleModuleInit]);

  const onMemedEvent = useCallback(
    <K extends keyof MemedPrescriptionEventMap>(
      event: K,
      handler: (data: MemedPrescriptionEventMap[K]) => void
    ): (() => void) => {
      const handlers =
        memedEventHandlersRef.current[event] ?? new Set<(data: unknown) => void>();
      handlers.add(handler as (data: unknown) => void);
      memedEventHandlersRef.current[event] = handlers;

      registerModuleInitListener();

      if (event === 'core:moduleHide') {
        if (window.MdSinapsePrescricao?.event?.add) {
          registerMemedEventListeners();
        }
      } else if (moduleReadyRef.current) {
        registerMemedEventListeners();
      }

      return () => {
        handlers.delete(handler as (data: unknown) => void);
        if (handlers.size === 0) {
          delete memedEventHandlersRef.current[event];
        }
      };
    },
    [registerModuleInitListener, registerMemedEventListeners]
  );

  const waitForModuleReady = useCallback(
    async (timeoutMs = 10000): Promise<boolean> => {
      if (moduleReadyRef.current) {
        return true;
      }

      if (typeof window !== 'undefined' && window.MdHub && window.MdSinapsePrescricao) {
        moduleReadyRef.current = true;
        scriptLoadedRef.current = true;
        setIsModuleReady(true);
        setIsScriptLoaded(true);
        registerMemedEventListeners();
        return true;
      }

      registerModuleInitListener();

      if (moduleReadyRef.current) {
        return true;
      }

      return new Promise((resolve) => {
        let settled = false;

        const onReady = () => {
          if (settled) {
            return;
          }
          settled = true;
          clearTimeout(timer);
          moduleInitWaitersRef.current = moduleInitWaitersRef.current.filter(
            (waiter) => waiter !== onReady
          );
          resolve(true);
        };

        const timer = setTimeout(() => {
          if (settled) {
            return;
          }
          settled = true;
          moduleInitWaitersRef.current = moduleInitWaitersRef.current.filter(
            (waiter) => waiter !== onReady
          );
          resolve(false);
        }, timeoutMs);

        moduleInitWaitersRef.current.push(onReady);
      });
    },
    [registerModuleInitListener, registerMemedEventListeners]
  );

  const addItemsToMemed = useCallback(
    async (items: AddItemPayload[]): Promise<AddItemsResult> => {
      if (!window.MdHub) {
        return { success: false, itemsSent: 0, error: 'MdHub não disponível' };
      }

      if (items.length === 0) {
        return { success: true, itemsSent: 0 };
      }

      const ready = await waitForModuleReady();
      if (!ready) {
        return { success: false, itemsSent: 0, error: MODULE_READY_TIMEOUT_ERROR };
      }

      let itemsSent = 0;

      try {
        for (const item of items) {
          await window.MdHub.command.send(PRESCRIPTION_MODULE, 'addItem', item);
          itemsSent++;
        }
        return { success: true, itemsSent };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao enviar itens da prescrição';
        return { success: false, itemsSent, error: errorMessage };
      }
    },
    [waitForModuleReady]
  );

  const newMemedPrescription = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    const ready = await waitForModuleReady();
    if (!ready) {
      return { success: false, error: MODULE_READY_TIMEOUT_ERROR };
    }

    try {
      await window.MdHub.command.send(PRESCRIPTION_MODULE, 'newPrescription');
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao criar nova prescrição';
      return { success: false, error: errorMessage };
    }
  }, [waitForModuleReady]);

  const viewMemedPrescription = useCallback(
    async (memedPrescriptionId: string): Promise<{ success: boolean; error?: string }> => {
      if (!window.MdHub) {
        return { success: false, error: 'MdHub não disponível' };
      }

      const ready = await waitForModuleReady();
      if (!ready) {
        return { success: false, error: MODULE_READY_TIMEOUT_ERROR };
      }

      try {
        await window.MdHub.command.send(PRESCRIPTION_MODULE, 'viewPrescription', memedPrescriptionId);
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao reimprimir prescrição';
        return { success: false, error: errorMessage };
      }
    },
    [waitForModuleReady]
  );

  const setMemedReceituarioTheme = useCallback(
    async (theme: number): Promise<{ success: boolean; error?: string }> => {
      if (!window.MdHub) {
        return { success: false, error: 'MdHub não disponível' };
      }

      if (!Number.isInteger(theme) || theme < RECEITUARIO_THEME_MIN || theme > RECEITUARIO_THEME_MAX) {
        return {
          success: false,
          error: `Tema do receituário inválido. Informe um número entre ${RECEITUARIO_THEME_MIN} e ${RECEITUARIO_THEME_MAX}.`,
        };
      }

      try {
        await window.MdHub.command.send(SDK_MODULE, 'find', {
          resource: `opcoes-receituario/ativar/${theme}`,
          cache: false,
        });
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao ativar tema do receituário';
        return { success: false, error: errorMessage };
      }
    },
    []
  );

  const setMemedPatient = useCallback(async (patientPayload: MemedPatientPayload): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    const validation = validateMemedPatient(patientPayload);
    if (!validation.valid) {
      return {
        success: false,
        error: `Dados obrigatórios faltando: ${validation.missingFields.join(', ')}`,
      };
    }

    try {
      setIsSettingPatient(true);
      console.info('[useMemedPrescription] Enviando comando setPaciente para o MdHub...', patientPayload);
      const sendPromise = window.MdHub.command.send(PRESCRIPTION_MODULE, 'setPaciente', patientPayload);
      const timeoutPromise = new Promise<{ timeout: boolean }>((resolve) => setTimeout(() => resolve({ timeout: true }), 6000));
      const res = await Promise.race([sendPromise, timeoutPromise]);
      if ((res as any)?.timeout) {
        console.warn('[useMemedPrescription] Timeout de 6s ao aguardar resposta de setPaciente, prosseguindo com abertura...');
      } else {
        console.info('[useMemedPrescription] ✓ setPaciente processado pelo MdHub');
      }
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao definir paciente';
      console.warn('[useMemedPrescription] Erro ao definir paciente:', errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsSettingPatient(false);
    }
  }, []);

  const setMemedPatientAllergies = useCallback(async (allergies: MedikeiAllergy[]): Promise<{ success: boolean; allergiesSent: number; unmapped: string[]; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, allergiesSent: 0, unmapped: [], error: 'MdHub não disponível' };
    }

    const { ids, unmapped } = mapAllergiesToMemedIds(allergies);

    try {
      setIsSettingAllergies(true);

      if (ids.length > 0) {
        await window.MdHub.command.send(PRESCRIPTION_MODULE, 'setAllergy', ids);
        return { success: true, allergiesSent: ids.length, unmapped };
      }

      const memedAllergies = mapAllergiesToMemed(allergies);

      if (memedAllergies.length === 0) {
        return { success: true, allergiesSent: 0, unmapped: [] };
      }

      let sentCount = 0;

      for (const allergy of memedAllergies) {
        await window.MdHub!.command.send(PRESCRIPTION_MODULE, 'setAllergy', allergy);
        sentCount++;
      }

      return { success: true, allergiesSent: sentCount, unmapped };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao definir alergias';
      return { success: false, allergiesSent: 0, unmapped, error: errorMessage };
    } finally {
      setIsSettingAllergies(false);
    }
  }, []);

  const setMemedWorkplace = useCallback(async (workplace: MemedWorkplace): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    try {
      await window.MdHub.command.send(PRESCRIPTION_MODULE, 'setWorkplace', workplace);
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao definir local de trabalho';
      return { success: false, error: errorMessage };
    }
  }, []);

  const setMemedFeatureToggles = useCallback(async (toggles: Record<string, boolean | string[]>): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    try {
      await window.MdHub.command.send(PRESCRIPTION_MODULE, 'setFeatureToggle', toggles);
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao configurar funcionalidades da prescrição';
      return { success: false, error: errorMessage };
    }
  }, []);

  const setMemedAdditionalData = useCallback(async (payload: MemedAdditionalDataPayload): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    try {
      await window.MdHub.command.send(PRESCRIPTION_MODULE, 'setAdditionalData', payload);
      return { success: true };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao definir dados adicionais da prescrição';
      return { success: false, error: errorMessage };
    }
  }, []);

  const openPrescriptionModule = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!window.MdHub) {
      return { success: false, error: 'MdHub não disponível' };
    }

    try {
      if (typeof document !== 'undefined') {
        document.body.classList.add('memed-module-open');
      }
      console.info('[useMemedPrescription] Chamando MdHub.module.show("plataforma.prescricao")...');
      const showPromise = window.MdHub.module.show(PRESCRIPTION_MODULE);
      const timeoutPromise = new Promise<{ timeout: boolean }>((resolve) => setTimeout(() => resolve({ timeout: true }), 4000));
      await Promise.race([Promise.resolve(showPromise), timeoutPromise]);
      console.info('[useMemedPrescription] ✓ MdHub.module.show disparado');
      return { success: true };
    } catch (err) {
      if (typeof document !== 'undefined') {
        document.body.classList.remove('memed-module-open');
      }
      const errorMessage = err instanceof Error ? err.message : 'Erro ao abrir módulo de prescrição';
      return { success: false, error: errorMessage };
    }
  }, []);

  const openPrescription = useCallback(
    async (
      patientPayload: MemedPatientPayload,
      workplace?: MemedWorkplace,
      allergies: MedikeiAllergy[] = [],
      chronicConditions: MedikeiChronicCondition[] = [],
      context: MemedPrescriptionContext = {},
      opts: MemedPrescriptionOptions = {}
    ): Promise<{ success: boolean; error?: string }> => {
      try {
        let token = opts.token || activeTokenRef.current || undefined;
        if (!token) {
          try {
            const tokenRes = await memedApi.getToken();
            if (tokenRes?.token) {
              token = tokenRes.token;
              activeTokenRef.current = token;
            }
          } catch (err) {
            console.warn('[useMemedPrescription] Falha ao obter token da MeMed na abertura da prescrição:', err);
          }
        } else {
          activeTokenRef.current = token;
        }

        console.info('[MEMED_DIAGNOSTIC] openPrescription iniciado para paciente:', patientPayload.nome, 'CPF:', patientPayload.cpf, 'Token ativo:', maskToken(token));

        const loaded = await loadScript(token);
        if (!loaded) {
          console.error('[MEMED_DIAGNOSTIC] ❌ loadScript falhou em openPrescription');
          return { success: false, error: 'Não foi possível carregar o script da MeMed' };
        }

        // Garante sincronização de token no objeto Sinapse antes dos comandos
        if (token) {
          try {
            const sinapse = (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao })
              .MdSinapsePrescricao;
            if (sinapse?.setToken) {
              sinapse.setToken(token);
            }
          } catch {
            // Silencioso
          }
        }

        // Aguarda o script inicializar o objeto window.MdHub e o módulo antes de enviar comandos
        const ready = await waitForModuleReady(15000);
        if (!ready) {
          console.warn('[useMemedPrescription] Timeout ao aguardar moduleReady, mas verificando window.MdHub...');
          if (!window.MdHub) {
            return { success: false, error: 'O módulo da MeMed ainda está inicializando. Tente clicar novamente em instantes.' };
          }
        }

        if (workplace) {
          const workplaceResult = await setMemedWorkplace(workplace);
          if (!workplaceResult.success) {
            return workplaceResult;
          }
        }

        if (opts.featureToggles && Object.keys(opts.featureToggles).length > 0) {
          const togglesResult = await setMemedFeatureToggles(opts.featureToggles);
          if (!togglesResult.success) {
            return togglesResult;
          }
        }

        if (opts.additionalData && opts.additionalData.header.length > 0) {
          const additionalDataResult = await setMemedAdditionalData(opts.additionalData);
          if (!additionalDataResult.success) {
            return additionalDataResult;
          }
        }

        const logContext = {
          patientId: patientPayload.idExterno,
          ...(workplace && (workplace.cnes || workplace.local_name)
            ? { workplaceId: workplace.cnes || workplace.local_name }
            : {}),
          ...context,
        };

        let patientWithConditions = patientPayload;
        try {
          const startedAt = Date.now();
          logConditionAlerts('STARTED', logContext);

          const categoriesConditions = mapClinicalConditionsToMemed(chronicConditions);
          if (categoriesConditions.length > 0) {
            patientWithConditions = { ...patientPayload, categoriesConditions };
          }

          const patientResult = await setMemedPatient(patientWithConditions);
          if (!patientResult.success) {
            logConditionAlerts('FAILED', { ...logContext, durationMs: Date.now() - startedAt });
            return patientResult;
          }

          logConditionAlerts('SUCCESS', { ...logContext, durationMs: Date.now() - startedAt });
        } catch {
          logConditionAlerts('FAILED', logContext);
          return { success: false, error: CONDITION_ALERTS_ERROR };
        }

        const allergiesResult = await setMemedPatientAllergies(allergies);
        if (!allergiesResult.success) {
          return { success: false, error: allergiesResult.error };
        }

        const moduleResult = await openPrescriptionModule();
        if (!moduleResult.success) {
          return moduleResult;
        }

        if (opts.receituarioTheme !== undefined) {
          const themeResult = await setMemedReceituarioTheme(opts.receituarioTheme);
          if (!themeResult.success) {
            return themeResult;
          }
        }

        if (opts.items && opts.items.length > 0) {
          const itemsResult = await addItemsToMemed(opts.items);
          if (!itemsResult.success) {
            return { success: false, error: itemsResult.error };
          }
        }

        console.info('[MEMED_DIAGNOSTIC] ✓ openPrescription finalizado com sucesso. Prescrição MeMed aberta para edição.');
        return { success: true };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Erro ao abrir prescrição';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [loadScript, waitForModuleReady, setMemedPatient, setMemedWorkplace, setMemedFeatureToggles, setMemedAdditionalData, setMemedPatientAllergies, openPrescriptionModule, setMemedReceituarioTheme, addItemsToMemed]
  );

  const initializeModule = useCallback(async (workplace: MemedWorkplace): Promise<boolean> => {
    let token: string | undefined;
    try {
      const tokenRes = await memedApi.getToken();
      if (tokenRes?.token) {
        token = tokenRes.token;
      }
    } catch {
      // Prossegue mesmo se não obtiver token
    }

    const loaded = await loadScript(token);
    if (!loaded) {
      return false;
    }

    if (!window.MdHub) {
      setError('MdHub não disponível');
      return false;
    }

    try {
      await window.MdHub.command.send(PRESCRIPTION_MODULE, 'setWorkplace', workplace);
      moduleReadyRef.current = true;
      setIsModuleReady(true);
      return true;
    } catch (err) {
      setError('Erro ao inicializar módulo');
      return false;
    }
  }, [loadScript]);

  const cleanupEventListeners = useCallback((): void => {
    if (typeof document !== 'undefined') {
      document.body.classList.remove('memed-module-open');
    }
    const sinapse = window.MdSinapsePrescricao;
    const mdHub = window.MdHub;

    registeredDispatchersRef.current.forEach((dispatcher, event) => {
      try {
        if (event === 'core:moduleHide' || event === 'core:moduleInit') {
          sinapse?.event?.remove?.(event, dispatcher);
        } else {
          mdHub?.event?.remove?.(event, dispatcher);
        }
      } catch {
        // Limpeza segura de listeners
      }
    });

    registeredDispatchersRef.current.clear();
    memedEventListenersRegisteredRef.current.clear();
    memedEventHandlersRef.current = {};
    moduleInitWaitersRef.current = [];
  }, []);

  useEffect(() => {
    if (moduleReadyRef.current) {
      registerMemedEventListeners();
    } else if (typeof window !== 'undefined' && (window.MdHub || (window as Window & typeof globalThis & { MdSinapsePrescricao?: MdSinapsePrescricao }).MdSinapsePrescricao)) {
      registerModuleInitListener();
    }

    const handleWindowUnload = () => {
      cleanupEventListeners();
    };

    // Previne que SecurityError de frames cross-origin da MeMed quebrem a aplicação React
    const handleCrossoriginSecurityError = (event: ErrorEvent): void => {
      const msg = event?.message || '';
      const source = event?.filename || '';
      if (
        (msg.includes('Blocked a frame with origin') || msg.includes('SecurityError') || msg.includes('cross-origin frame')) &&
        (msg.includes('memed') || source.includes('memed') || source.includes('sinapse') || source.includes('plataforma'))
      ) {
        console.warn('[useMemedPrescription] Suprimido SecurityError cross-origin da MeMed:', msg);
        event.preventDefault?.();
        event.stopPropagation?.();
      }
    };

    const handleCrossoriginRejection = (event: PromiseRejectionEvent) => {
      const reason = event?.reason;
      const msg = typeof reason === 'string' ? reason : reason?.message || '';
      if (
        (msg.includes('Blocked a frame with origin') || msg.includes('SecurityError') || msg.includes('cross-origin frame')) &&
        (msg.includes('memed') || msg.includes('integrations'))
      ) {
        console.warn('[useMemedPrescription] Suprimido PromiseRejection SecurityError da MeMed:', msg);
        event.preventDefault?.();
      }
    };

    window.addEventListener('beforeunload', handleWindowUnload);
    window.addEventListener('error', handleCrossoriginSecurityError, true);
    window.addEventListener('unhandledrejection', handleCrossoriginRejection);

    return () => {
      window.removeEventListener('beforeunload', handleWindowUnload);
      window.removeEventListener('error', handleCrossoriginSecurityError, true);
      window.removeEventListener('unhandledrejection', handleCrossoriginRejection);
      cleanupEventListeners();
    };
  }, [cleanupEventListeners, registerMemedEventListeners, registerModuleInitListener]);

  return {
    loadScript,
    setMemedPatient,
    setMemedPatientAllergies,
    setMemedWorkplace,
    setMemedFeatureToggles,
    setMemedAdditionalData,
    openPrescription,
    openPrescriptionModule,
    initializeModule,
    registerModuleInitListener,
    waitForModuleReady,
    addItemsToMemed,
    newMemedPrescription,
    viewMemedPrescription,
    onMemedEvent,
    setMemedReceituarioTheme,
    isScriptLoaded,
    isModuleReady,
    isSettingPatient,
    isSettingAllergies,
    error,
    setError,
  };
}

export type { MemedPatientPayload, MemedPrescriptionContext, MemedPrescriptionOptions };
export { mapPatientToMemed, validateMemedPatient, CONDITION_ALERTS_ERROR };